import { useQuery } from "@tanstack/react-query";

import {
  DashboardWardSelection,
  IDashboardCategoryValue,
  IDashboardDateRange,
  IDashboardKpi,
  IDashboardKpiSet,
  IDashboardWardArchivedRank,
  IDashboardWardRank,
} from "../../../@types/admin";
import { adminDashboardService } from "../../../services/admin";
import { ensureOk } from "../../../utils/ensureOk";

function rangeKey(range: IDashboardDateRange): string {
  return `${range.startDate.toISOString()}_${range.endDate.toISOString()}`;
}

function selectionKey(selection: DashboardWardSelection): string {
  if (selection.length === 0) return "all";
  return [...selection].sort((a, b) => a - b).join(",");
}

function withFormatter(
  kpi: IDashboardKpi,
  formatter: IDashboardKpi["formatter"]
): IDashboardKpi {
  return { ...kpi, formatter };
}

function applyKpiFormatters(set: IDashboardKpiSet): IDashboardKpiSet {
  return {
    activePatients: withFormatter(set.activePatients, "number"),
    createdPatients: withFormatter(set.createdPatients, "number"),
    avgWardDays: withFormatter(set.avgWardDays, "days"),
    workflowsTriggered: withFormatter(set.workflowsTriggered, "number"),
  };
}

export function useDashboardKpis(
  wardIds: DashboardWardSelection,
  range: IDashboardDateRange
) {
  return useQuery<IDashboardKpiSet>({
    queryKey: ["admin-dashboard-kpis", selectionKey(wardIds), rangeKey(range)],
    queryFn: async () => {
      const response = await adminDashboardService.getKpis(wardIds, range);
      return applyKpiFormatters(ensureOk(response));
    },
  });
}

export function useActivePatientsPerWard(
  wardIds: DashboardWardSelection,
  range: IDashboardDateRange
) {
  return useQuery<IDashboardWardRank[]>({
    queryKey: [
      "admin-dashboard-active-per-ward",
      selectionKey(wardIds),
      rangeKey(range),
    ],
    queryFn: async () => {
      const response = await adminDashboardService.getActivePatientsPerWard(
        wardIds,
        range
      );
      return ensureOk(response) ?? [];
    },
  });
}

export function useArchivedPatientsPerWard(
  wardIds: DashboardWardSelection,
  range: IDashboardDateRange
) {
  return useQuery<IDashboardWardArchivedRank[]>({
    queryKey: [
      "admin-dashboard-archived-per-ward",
      selectionKey(wardIds),
      rangeKey(range),
    ],
    queryFn: async () => {
      const response = await adminDashboardService.getArchivedPatientsPerWard(
        wardIds,
        range
      );
      return ensureOk(response) ?? [];
    },
  });
}

export function useDeviceUsage(
  wardIds: DashboardWardSelection,
  range: IDashboardDateRange
) {
  return useQuery<IDashboardCategoryValue[]>({
    queryKey: [
      "admin-dashboard-device-usage",
      selectionKey(wardIds),
      rangeKey(range),
    ],
    queryFn: async () => {
      const response = await adminDashboardService.getDeviceUsage(
        wardIds,
        range
      );
      return ensureOk(response) ?? [];
    },
  });
}

export function useReferralSources(
  wardIds: DashboardWardSelection,
  range: IDashboardDateRange
) {
  return useQuery<IDashboardCategoryValue[]>({
    queryKey: [
      "admin-dashboard-referral-sources",
      selectionKey(wardIds),
      rangeKey(range),
    ],
    queryFn: async () => {
      const response = await adminDashboardService.getReferralSources(
        wardIds,
        range
      );
      return ensureOk(response) ?? [];
    },
  });
}

export function usePromResults(
  wardIds: DashboardWardSelection,
  range: IDashboardDateRange
) {
  return useQuery<IDashboardCategoryValue[]>({
    queryKey: [
      "admin-dashboard-prom-results",
      selectionKey(wardIds),
      rangeKey(range),
    ],
    queryFn: async () => {
      const response = await adminDashboardService.getPromResults(
        wardIds,
        range
      );
      return ensureOk(response) ?? [];
    },
  });
}
